import objectIsEmpty from "common/utils/objectIsEmpty";
import uid from "common/utils/uid";
import type { DocMigration } from "../docMigration";

// eslint-disable-next-line @typescript-eslint/naming-convention
const migration_0_0_105: DocMigration = {
  maxSchemaVersion: "0.0.105",
  description: "migrate foundation settings to use settings sets",
  apply(json: any) {
    repairMissingBlockIds(json);
    migrateFoundationPlanSettingsToPlanSettings(json);
    migrateFoundationSettingsToFoundationSetting(json);

    return json;
  },
};

function repairMissingBlockIds(json: any) {
  if (json.blocks) {
    for (const blockId in json.blocks) {
      if (json.blocks[blockId]?.id !== blockId) {
        json.blocks[blockId].id = blockId;
      }
    }
  }

  return json;
}

function migrateFoundationPlanSettingsToPlanSettings(json: any) {
  if (!json.settings || !json.settingsSets) {
    return json;
  }

  const foundationSettings = {
    FOUNDATION_SETTINGS: "FOUNDATION_SETTINGS",
  };

  const foundationSuperSettings = {
    FOUNDATION_SLAB_SETTINGS: "FOUNDATION_SLAB_SETTINGS",
    FOUNDATION_RAISED_SLAB_SETTINGS: "FOUNDATION_RAISED_SLAB_SETTINGS",
  };

  const foundationSubSettings = {
    FOUNDATION_SLAB_SETTINGS_MONO: "FOUNDATION_SLAB_SETTINGS_MONO",
    FOUNDATION_SLAB_SETTINGS_STEM_CMU: "FOUNDATION_SLAB_SETTINGS_STEM_CMU",
    FOUNDATION_SLAB_SETTINGS_STEM_CIP: "FOUNDATION_SLAB_SETTINGS_STEM_CIP",
    FOUNDATION_RAISED_SLAB_SETTINGS_STEM_CMU: "FOUNDATION_RAISED_SLAB_SETTINGS_STEM_CMU",
  };

  const foundationPlanSetKeys = {
    includeFootingInfo: true,
    includeDetailCallouts: true,
    dimensionPlumbingProps: true,
    dimensionElectricalFloorProps: true,
    dimensionBrickLedge: true,
    dimensionInteriorFootings: true,
  };

  const planSettingsType = "PLAN_SETTINGS";

  const { settings } = json;

  const planSettingsByParentId: { [parentId: string]: any } = {};

  const doMigration = (setting: any) => {
    const planSettings = {};

    let update = false;
    for (const key in foundationPlanSetKeys) {
      if (setting[key] !== undefined) {
        planSettings[key] = setting[key];
        delete setting[key];
        update = true;
      }
    }

    if (update) {
      const { parentId } = setting;
      if (planSettingsByParentId[parentId]) {
        planSettingsByParentId[parentId] = {
          ...planSettingsByParentId[parentId],
          ...planSettings,
        };
      } else {
        planSettingsByParentId[parentId] = {
          id: uid(),
          parentId,
          type: planSettingsType,
          entityType: "SETTINGS",
          sortIndex: 0,
          ...planSettings,
        };
      }
    }
  };

  // extract existing plan settings if they exist

  for (const settingsId in settings) {
    const setting = settings[settingsId];
    if (setting.type === planSettingsType) {
      const { parentId } = setting;
      planSettingsByParentId[parentId] = setting;
      continue;
    }
  }

  // migration from tree root down - more specific settings get priority

  for (const settingsId in settings) {
    const setting = settings[settingsId];
    if (foundationSettings[setting.type]) {
      doMigration(setting);
    }
  }

  for (const settingsId in settings) {
    const setting = settings[settingsId];
    if (foundationSuperSettings[setting.type]) {
      doMigration(setting);
    }
  }

  for (const settingsId in settings) {
    const setting = settings[settingsId];
    if (foundationSubSettings[setting.type]) {
      doMigration(setting);
    }
  }

  // write new plan settings to settings

  for (const parentId in planSettingsByParentId) {
    const setting = planSettingsByParentId[parentId];
    const { id } = setting;
    json.settings[id] = setting;
  }

  return json;
}

function migrateFoundationSettingsToFoundationSetting(json: any) {
  const foundationSettingsMapping = {
    FOUNDATION_SETTINGS: "FOUNDATION_SETTINGS", // foundation settings remains in place
    FOUNDATION_SLAB_SETTINGS: "SLAB_ON_GRADE",
    FOUNDATION_RAISED_SLAB_SETTINGS: "RAISED_SLAB",
    FOUNDATION_SLAB_SETTINGS_MONO: "SLAB_ON_GRADE.MONOLITHIC",
    FOUNDATION_SLAB_SETTINGS_STEM_CMU: "SLAB_ON_GRADE.STEM_WALL_CMU",
    FOUNDATION_SLAB_SETTINGS_STEM_CIP: "SLAB_ON_GRADE.STEM_WALL_CIP",
    FOUNDATION_RAISED_SLAB_SETTINGS_STEM_CMU: "RAISED_SLAB.STEM_WALL_CMU",
  };

  // some new naming conventions for code consistency
  if (json.foundations) {
    for (const foundationId in json.foundations) {
      const foundation = json.foundations[foundationId];
      if (foundation.type !== undefined) {
        foundation.superType = foundation.type;
        delete foundation.type;
      }
    }
  }

  if (json.settings) {
    const foundationSettingsByParentId: { [parentId: string]: any[] } = {};

    for (const settingId in json.settings) {
      const setting = json.settings[settingId];
      if (foundationSettingsMapping[setting.type]) {
        // some new naming conventions for code consistency
        if (setting.foundationSystemType !== undefined) {
          setting.foundationSuperSystemType = setting.foundationSystemType;
          delete setting.foundationSystemType;
        }

        const { parentId } = setting;
        if (!foundationSettingsByParentId[parentId]) {
          foundationSettingsByParentId[parentId] = [];
        }

        foundationSettingsByParentId[parentId].push(setting);
      }
    }

    for (const parentId in foundationSettingsByParentId) {
      const settings = foundationSettingsByParentId[parentId];

      let sourceSetting = settings.find((s) => s.type === foundationSettingsMapping.FOUNDATION_SETTINGS);
      if (!sourceSetting) {
        // Found an orphaned foundation setting.
        // This can either be as a result of a deliberate user decision to only override
        // sub foundation type settings, or because the original parent FOUNDATION_SETTING
        // was deleted and we did not delete the sub foundation settings (as a result of bug).
        //
        // As a precaution, the orphaned setting
        // will be left in place on a new FOUNDATION_SETTING object, and will need to be deleted
        // manually if that was the intention. This will not affect the building generation,
        // but will expose the orphaned setting to the user.
        sourceSetting = {
          id: uid(),
          parentId,
          type: foundationSettingsMapping.FOUNDATION_SETTINGS,
          entityType: "SETTINGS",
          sortIndex: 0,
        };
      }

      for (let i = 0; i < settings.length; i++) {
        const setting = settings[i];
        if (setting.id === sourceSetting.id) {
          continue;
        }

        // convert this setting from a SettingFragment to an internal foundation setting;

        const settingId = foundationSettingsMapping[setting.type];

        delete json.settings[setting.id];

        delete setting.id;
        delete setting.type;
        delete setting.parentId;
        delete setting.entityType;
        delete setting.sortIndex;

        if (objectIsEmpty(setting)) {
          continue;
        }

        setting.id = settingId;
        sourceSetting[settingId] = setting;
      }

      json.settings[sourceSetting.id] = sourceSetting;
    }
  }
}

export default migration_0_0_105;
